import debug from "debug";
import { nanoid } from "nanoid";
import AddAction from "./AddAction";
import TransformAction from "./TransformAction";
import EditReplaceAction from "./EditReplaceAction";

const log = debug("app:ur");

/**
 * Manages undo and redo functionality within the application.
 * @category Components
 * @subcategory UndoRedoManager
 */
export default class UndoRedoManager {
  static instance = null;

  /**
   * Supported action types for transformations.
   * @type {string[]}
   */
  static TRANSFORM_ACTIONS = ["move", "rotate", "edit", "delete", "replace"];

  /**
   * Constructor for UndoRedoManager.
   * Ensures a singleton instance is created and shared across the application.
   * @param {Object} experience - The main application experience object.
   */
  constructor(experience) {
    if (UndoRedoManager.instance) {
      return UndoRedoManager.instance;
    }

    this.experience = experience;
    this.eventHub = experience.eventHub;

    // Initialize the vault once in the constructor
    this.vault = {
      eventLog: new Map(),
      objectIdMap: new Map(),
      currentEventIndex: -1,
      objectCounter: 0,
      eventCounter: 0,
    };

    this.editReplaceAction = new EditReplaceAction(this.experience);
    this.experience.undoRedoVault = this.vault;
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.setupListeners();
    UndoRedoManager.instance = this;
  }

  /**
   * Singleton instance getter.
   * @param {Object} experience - The main application experience object.
   * @returns {UndoRedoManager} - Singleton instance.
   */
  static getInstance(experience) {
    if (!UndoRedoManager.instance) {
      UndoRedoManager.instance = new UndoRedoManager(experience);
    }
    return UndoRedoManager.instance;
  }

  /**
   * Sets up listeners for events such as undo, redo, and custom actions.
   */
  setupListeners() {
    this.eventHub.on("MakePartsEvent", (payload) =>
      this.init(payload, "MakePartsEvent")
    );
    this.eventHub.on("MakePartsLoaderEvent", (payload) =>
      this.init(payload, "MakePartsLoaderEvent")
    );
    this.eventHub.on("logEvent", (payload) => this.init(payload, "logEvent"));
    this.eventHub.on("TextBoxEvent", (payload) =>
      this.init(payload, "TextBoxEvent")
    );
    this.eventHub.on("undo", () => this.handleAction("undo"));
    this.eventHub.on("redo", () => this.handleAction("redo"));
    this.eventHub.on("globalKeyDown", this.handleKeyDown);

  }

  cleanup() {
    if (!this.eventHub) return; // Prevent cleanup if already disposed

    log("Cleaning up UndoRedoManager...");

    // Unregister all event listeners
    this.eventHub.off("MakePartsEvent", this.init);
    this.eventHub.off("MakePartsLoaderEvent", this.init);
    this.eventHub.off("logEvent", this.init);
    this.eventHub.off("TextBoxEvent", this.init);
    this.eventHub.off("undo", () => this.handleAction("undo"));
    this.eventHub.off("redo", () => this.handleAction("redo"));
    this.eventHub.off("globalKeyDown", this.handleKeyDown);

    log("Unregistered all event listeners");

    // Clear undo/redo vault data
    this.vault.eventLog.clear();
    this.vault.objectIdMap.clear();
    this.vault.currentEventIndex = -1;
    this.vault.objectCounter = 0;
    this.vault.eventCounter = 0;

    log("Cleared undo/redo vault data");

    // Reset singleton instance
    UndoRedoManager.instance = null;

    // Clear references
    this.experience = null;
    this.eventHub = null;
    this.vault = null;
    this.editReplaceAction = null;

    log("UndoRedoManager cleanup completed.");
  }

  /**
   * Initializes a new event and logs it in the vault.
   * @param {Object} payload - The event payload containing eventType and data.
   * @param {string} eventName - The name of the event.
   */
  init(payload, eventName) {
    const { eventType, data } = payload;

    // ---- Load (loadProject / view design / mode-switch) ----
    if (eventName === "MakePartsLoaderEvent" && eventType === "load") {
      // Mode switch: same design, just 2D↔3D — preserve undo history and objectIds
      // so undo/redo still targets the same logical objects after scene rebuild
      if (payload.reason === "mode-switch") {
        this.processMakePartsLoaderLog(payload);
        return;
      }
      // Actual load (file or view design): new baseline, clear history
      this.clearHistory("baseline: loadProject");
      const enrichedBaseline = this.enrichPayloadWithObjectIds(payload);
      this.processMakePartsLoaderLog(enrichedBaseline);
      return;
    }
    // -----------------------------------------

    // (the rest of your original init method stays the same)
    const eventId = nanoid();

    log("payload, eventName", payload, eventName);

    if (this.vault.currentEventIndex < this.vault.eventLog.size - 1) {
      const eventIds = Array.from(this.vault.eventLog.keys());
      eventIds.slice(this.vault.currentEventIndex + 1).forEach((id) => {
        this.vault.eventLog.delete(id);
      });
    }

    const enrichedPayload = UndoRedoManager.TRANSFORM_ACTIONS.includes(eventType)
      ? payload
      : this.enrichPayloadWithObjectIds(payload);

    this.setEventLog(eventId, eventType, enrichedPayload.data);
    this.routeEvent(eventName, enrichedPayload);
  }


  /**
   * Logs an event in the vault.
   * @param {string} eventId - The unique ID for the event.
   * @param {string} eventType - The type of the event.
   * @param {Object} data - The event data.
   */
  setEventLog(eventId, eventType, data) {
    this.vault.eventLog.set(eventId, { eventType, data });
    this.vault.currentEventIndex = this.vault.eventLog.size - 1;
  }

  /**
   * Handles keyboard shortcuts for undo and redo.
   * @param {KeyboardEvent} event - The keyboard event object.
   */
  handleKeyDown(event) {
    const key = event.key.toLowerCase();
    if ((event.ctrlKey || event.metaKey) && key === "z") {
      event.preventDefault();
      event.shiftKey ? this.handleAction("redo") : this.handleAction("undo");
    } else if ((event.ctrlKey || event.metaKey) && key === "y") {
      event.preventDefault();
      this.handleAction("redo");
    }
  }

  /**
   * Enriches payload data with unique object IDs.
   * @param {Object} payload - The payload to enrich.
   * @returns {Object} - The enriched payload.
   */
  enrichPayloadWithObjectIds(payload) {
    const { data } = payload;
    return {
      ...payload,
      data: {
        ...data,
        figures:
          data.figures?.map((figure) => ({
            ...figure,
            partData: {
              ...figure.partData,
              objectId: nanoid(),
            },
          })) || [],
        others:
          data.others?.map((other) => ({
            ...other,
            objectId: nanoid(),
          })) || [],
      },
    };
  }

  /**
   * Routes the event to the appropriate handler.
   * @param {string} eventName - The name of the event.
   * @param {Object} enrichedPayload - The enriched payload.
   */
  routeEvent(eventName, enrichedPayload) {
    const eventHandlers = {
      MakePartsEvent: this.processMakePartsLog.bind(this),
      MakePartsLoaderEvent: this.processMakePartsLoaderLog.bind(this),
      logEvent: this.processEvents.bind(this),
      TextBoxEvent: this.processMakePartsLog.bind(this),
    };

    const handler = eventHandlers[eventName];
    if (handler) {
      handler(enrichedPayload);
    } else {
      log("Unhandled event type:", eventName);
    }
  }

  /**
   * Processes a generic event.
   * @param {Object} payload - The event payload.
   */
  processEvents(payload) {
    try {
      const { eventType } = payload;

      if (["replace", "edit"].includes(eventType)) {
        this.editReplaceAction.handleAction(payload);
      } else {
        // Handle other event types as needed
      }
    } catch (error) {
      log("Error processing event:", error.message);
      console.error("Detailed error:", error);
    }
  }

  processMakePartsLog(payload) {
    log("payload at MakePartsLog", payload);
    const { eventType, data } = payload;
    if (!data.figures) {
      log("Error: Missing figures in payload data.");
      return;
    }
    this.eventHub.emit("makeParts", data);
  }

  async processMakePartsLoaderLog(payload) {
    log("payload at MakePartsLog", payload);
    const { eventType, data } = payload;
    if (!data.figures) {
      log("Error: Missing figures in payload data.");
      return;
    }
    this.eventHub.emit("makePartsLoader", data);
  }

  handleAction(actionType) {
    const isUndo = actionType === "undo";
    const isRedo = actionType === "redo";

    if (
      (isUndo && this.vault.currentEventIndex < 0) ||
      (isRedo && this.vault.currentEventIndex >= this.vault.eventLog.size - 1)
    ) {
      return;
    }

    try {
      const eventIds = Array.from(this.vault.eventLog.keys());
      const eventId = isUndo
        ? eventIds[this.vault.currentEventIndex]
        : eventIds[this.vault.currentEventIndex + 1];

      const event = this.vault.eventLog.get(eventId);
      if (!event) return;

      const actionHandler = UndoRedoManager.TRANSFORM_ACTIONS.includes(
        event.eventType
      )
        ? new TransformAction(this.experience)
        : new AddAction(this.experience);

      actionHandler.init(event.eventType, actionType, eventId);

      this.vault.currentEventIndex += isUndo ? -1 : 1;

      this.eventHub.emit("needsRender", "true");
    } catch (error) {
      console.error(`${actionType} operation failed:`, error);
    }
  }

  clearHistory(reason = "") {
    try {
      this.vault.eventLog.clear();
      this.vault.objectIdMap.clear();
      this.vault.currentEventIndex = -1;
      this.vault.objectCounter = 0;
      this.vault.eventCounter = 0;
      log(`[undo/redo] history cleared ${reason ? `(${reason})` : ""}`);
    } catch (e) {
      console.warn("Failed to clear history:", e);
    }
  }

}
